import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  inject,
  effect,
  Output,
  EventEmitter,
  output,
} from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { ScheduleService } from '../../services/schedule.service';
import { CalendarDay } from '../../models/schedule-interfaces';
import { SchemaMembershipStateService } from 'libs/core';
import { DataList, IconComponent, Schedule } from '@net3-multiplatform-client/libs';
import { ScheduleBadgeComponent } from '../schedule-badge/schedule-badge';

@Component({
  selector: 'lib-schedule-calendar',
  imports: [CommonModule, NgClass, IconComponent, ScheduleBadgeComponent],
  templateUrl: './schedule-calendar.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleCalendar {
  private schemaState = inject(SchemaMembershipStateService);

  //@ Input del sector per consultar
  sectorId = input.required<string>();
  schedules = input.required<Schedule[]>();

  //Output per enviar cap a la pàgina
  dateChange = output<string>();

  // @ Determinar si el reg és per volum des de les propietats del schema
  protected readonly isByVolume = computed(() => {
    const props = this.schemaState.properties() as any;
    const byVolume = props?.irrigationScheduleInVolume ?? false;
    return byVolume;
  });

  constructor() {
    effect(
      () => {
        this.dateChange.emit(this.requestDate());
      }
    );
  }

  //funció per agafar la data al effect
  private readonly requestDate = computed(() => {
    const d = this.viewDate();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${yyyy}`; // MM-YYYY
  });

  //! constants limit navegació
  private readonly today = new Date();
  private readonly minDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth() - 3,
    1
  );
  private readonly maxDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth() + 3,
    1
  );

  viewDate = signal(
    new Date(this.today.getFullYear(), this.today.getMonth(), 1)
  );

  daysOfWeek = ['1', '2', '3', '4', '5', '6', '7'];
  monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  // @ Nom del mes i any actuals derivats de viewDate
  currentMonthName = computed(
    () => this.monthNames[this.viewDate().getMonth()]
  );
  currentYear = computed(() => this.viewDate().getFullYear());

  private readonly ScheduleMap = computed(() => {
    const map = new Map<string, string>();
    const byVolume = this.isByVolume();

    this.schedules().forEach((s) => {
      let displayValue: string;

      if (byVolume) {
        //@ volum en metres cúbics
        displayValue =
          s.value >= 1000 ? `${(s.value / 1000).toFixed(1)}k` : `${s.value} m³`;
      } else {
        //@ format temps HH:mm
        const hours = Math.floor(s.value / 60);
        const minutes = s.value % 60;
        displayValue = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}`;
      }

      map.set(s.irrigationDate, displayValue);
    });
    return map;
  });

  //! Càlcul dies del calendari
  calendarDays = computed<CalendarDay[]>(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();
    const schedule = this.ScheduleMap();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    //! Ajustem perquè la setmana comenci en dilluns (1) i no diumenge (0)
    let startDay = firstDayOfMonth.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;

    const days = [];

    //@ dies del mes anterior per omplir l'inici
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      days.push(this.createDayObject(d, false, schedule));
    }

    //@ dies del mes actual
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push(this.createDayObject(d, true, schedule));
    }

    //@ dies del mes següent per completar només última fila
    let lastDay = lastDayOfMonth.getDay();
    lastDay = lastDay === 0 ? 6 : lastDay - 1; // Ajustem a Dilluns (0-6)

    const remainingDays = 6 - lastDay;
    if (remainingDays > 0) {
      for (let i = 1; i <= remainingDays; i++) {
        const d = new Date(year, month + 1, i);
        days.push(this.createDayObject(d, false, schedule));
      }
    }

    return days;
  });

  //? comprovació de limits per activar/desactivar botons
  canGoPrev = computed(() => {
    const prev = new Date(
      this.viewDate().getFullYear(),
      this.viewDate().getMonth() - 1,
      1
    );
    return prev >= this.minDate;
  });

  canGoNext = computed(() => {
    const next = new Date(
      this.viewDate().getFullYear(),
      this.viewDate().getMonth() + 1,
      1
    );
    return next <= this.maxDate;
  });

  //@ accions per canviar de mes
  prevMonth() {
    if (this.canGoPrev()) {
      this.viewDate.update(
        (d) => new Date(d.getFullYear(), d.getMonth() - 1, 1)
      );
    }
  }

  nextMonth() {
    if (this.canGoNext()) {
      this.viewDate.update(
        (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1)
      );
    }
  }

  //@ helper per crear l'objecte de dia
  private createDayObject(
    date: Date,
    isCurrentMonth: boolean,
    schedule: Map<string, string>
  ): CalendarDay {
    const isToday = this.isSameDay(date, this.today);
    const isPast = !isToday && date < this.today;

    // @ Format de data YYYY-MM-DD 
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // @ busquem si hi ha reg previst per aquest dia
    const value = schedule.get(dateStr) || '--:--';

    return {
      dayNumber: date.getDate(),
      isCurrentMonth,
      isToday,
      isPast,
      date: dateStr,
      value,
    };
  }

  //? comprovació si dues dates són el mateix dia
  private isSameDay(d1: Date, d2: Date) {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }
}
